import { call, put, fork, take } from "redux-saga/effects";
import { eventChannel, END, buffers } from "redux-saga";
import { eventAlbumSelectionUploadConstants as C } from "../actions/constants/eventAlbumSelectionUploadConstants";
import { eventAlbumSelectionConstants as albumSelectionC } from "../actions/constants/eventAlbumSelectionConstants";
import { commonConstants } from "../actions/constants/commonConstants";
import { uploadAlbumImages, listAlbumCuratedMedia } from "../services/eventAlbumSelectionUploadService";
import type { AlbumUploadProgress } from "../services/eventAlbumSelectionUploadService";

function* watchAlbumUploadProgress(channel: any): any {
  try {
    while (true) {
      const action = yield take(channel);
      yield put(action);
    }
  } finally {
    // channel closed
  }
}

function* uploadAlbumImagesSaga(action: any): any {
  const { files, studioId, eventId, serviceId = null, templateId, itemIndex = null } = action.payload ?? {};
  try {

    if (!files?.length) {
      yield put({ type: C.uploadAlbumImagesFailure, payload: "No files selected for upload" });
      yield put({ type: commonConstants.showErrorMessage, payload: "No files selected for upload" });
      return;
    }
    if (!eventId) {
      yield put({ type: C.uploadAlbumImagesFailure, payload: "Event ID is missing" });
      yield put({ type: commonConstants.showErrorMessage, payload: "Event ID is missing. Open this step from an existing event." });
      return;
    }
    if (!templateId) {
      yield put({ type: C.uploadAlbumImagesFailure, payload: "Template ID is required for album upload" });
      yield put({ type: commonConstants.showErrorMessage, payload: "Template ID is required for album upload" });
      return;
    }

    let progressEmitter: any;
    const progressChannel = eventChannel((emitter) => {
      progressEmitter = emitter;
      return () => {};
    }, buffers.expanding(100));

    yield fork(watchAlbumUploadProgress, progressChannel);

    // Seed all files at 0%
    for (const file of files) {
      const fileName: string = (file.originFileObj as File | undefined)?.name ?? file.name;
      yield put({ type: C.uploadAlbumImagesProgress, payload: { templateId, studioId, eventId, serviceId, fileName, percentage: 0 } });
    }

    const result = yield call(
      uploadAlbumImages,
      files,
      studioId,
      eventId,
      serviceId,
      templateId,
      itemIndex,
      {
        onProgress: (progress: AlbumUploadProgress) => {
          if (progressEmitter) {
            progressEmitter({
              type: C.uploadAlbumImagesProgress,
              payload: { templateId, studioId, eventId, serviceId, fileName: progress.fileName, percentage: progress.percentage },
            });
          }
        },
      }
    );

    progressChannel.close();

    yield put({ type: C.uploadAlbumImagesSuccess, payload: { ...result, templateId, studioId, eventId, serviceId } });

    if (result.success.length) {
      yield put({ type: commonConstants.showSuccessMessage, payload: `${result.success.length} photo${result.success.length > 1 ? "s" : ""} uploaded successfully` });
    }
    if (result.failed.length) {
      yield put({ type: commonConstants.showErrorMessage, payload: `${result.failed.length} photo${result.failed.length > 1 ? "s" : ""} failed to upload` });
    }

    yield put({
      type: albumSelectionC.listCuratedRequest,
      payload: { studioId, eventId, serviceId, templateId, limit: 50 },
    });
  } catch (error: any) {
    yield put({ type: C.uploadAlbumImagesFailure, payload: { templateId, message: error?.message || "Failed to upload album photos" } });
    yield put({ type: commonConstants.showErrorMessage, payload: error?.message || "Failed to upload album photos" });
  }
}

function* listAlbumCuratedMediaSaga(action: any): any {
  try {
    const { studioId, eventId, serviceId = null, templateId, itemIndex = null, limit = 50, continuationToken = null, append = false } = action.payload;

    const response: any = yield call(listAlbumCuratedMedia, {
      studioId,
      eventId,
      serviceId,
      templateId,
      itemIndex,
      limit,
      continuationToken,
    });

    if (!response?.success) {
      throw new Error(response?.message || response?.errorMessage || "Failed to load curated media");
    }

    yield put({
      type: C.listAlbumCuratedMediaSuccess,
      payload: {
        serviceId,
        templateId,
        itemIndex,
        items: response.resultObject?.items || [],
        total: response.resultObject?.total ?? 0,
        nextContinuationToken: response.resultObject?.nextContinuationToken ?? null,
        hasMore: response.resultObject?.hasMore ?? false,
        append,
      },
    });
  } catch (error: any) {
    yield put({ type: C.listAlbumCuratedMediaFailure, payload: { templateId: action.payload?.templateId, message: error?.message } });
    yield put({ type: commonConstants.showErrorMessage, payload: error?.message || "Failed to load curated media" });
  }
}

export const eventAlbumSelectionUploadSaga = {
  uploadAlbumImages: uploadAlbumImagesSaga,
  listAlbumCuratedMedia: listAlbumCuratedMediaSaga,
};
